import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import ImageUpload from "@/components/ImageUpload";
import { toast } from "sonner";

const NONE = "__none__";

const CategoryDialog = ({ open, onClose, onSaved, menuId, category, menus = [], parentId = null, hasChildren = false }) => {
  const [name, setName] = useState(category?.name || "");
  const [description, setDescription] = useState(category?.description || "");
  const [cover, setCover] = useState(category?.cover_image || null);
  const [destMenu, setDestMenu] = useState(category?.menu_id || menuId);
  const [parent, setParent] = useState(category?.parent_id || parentId || NONE);
  const [parentOptions, setParentOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/menus/${destMenu}/categories`).then((r) => {
      if (!alive) return;
      const opts = (r.data || []).filter((c) => !c.parent_id && c.id !== category?.id);
      setParentOptions(opts);
      // se il genitore selezionato non è più valido nel menù scelto, azzera
      if (parent !== NONE && !opts.some((c) => c.id === parent)) setParent(NONE);
    }).catch(() => setParentOptions([]));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destMenu]);

  const save = async () => {
    if (!name.trim()) return toast.error("Inserisci un nome");
    setSaving(true);
    try {
      const payload = {
        menu_id: destMenu, name, description, cover_image: cover,
        visible: category?.visible !== false,
        parent_id: hasChildren ? null : (parent === NONE ? null : parent),
      };
      if (category) await api.put(`/categories/${category.id}`, payload);
      else await api.post("/categories", payload);
      toast.success("Salvato");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const multiMenus = menus.length > 1;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="p-0 gap-0 !flex flex-col max-h-[90vh]" data-testid="category-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0 text-left"><DialogTitle>{category ? "Modifica" : (parentId ? "Nuova sottocategoria" : "Nuova")} categoria</DialogTitle><DialogDescription>Nome, descrizione, posizione (menù e annidamento) e foto di copertina della categoria.</DialogDescription></DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
          <div>
            <label className="text-sm font-medium">Nome</label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="category-name-input" placeholder="Es. Antipasti, Primi…" className="mt-1 w-full rounded-xl border border-input px-4 py-2.5 focus:outline-none focus:border-[#002FA7]" />
          </div>

          <div>
            <label className="text-sm font-medium">Descrizione <span className="text-xs text-muted-foreground font-normal">(opzionale)</span></label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} data-testid="category-description-input" rows={2} placeholder="Breve testo mostrato sotto il titolo nel menù pubblico" className="mt-1 w-full rounded-xl border border-input px-4 py-2.5 focus:outline-none focus:border-[#002FA7]" />
            <p className="mt-1 text-[11px] text-muted-foreground">Verrà tradotta automaticamente con le altre voci (report in Impostazioni).</p>
          </div>

          <div className="rounded-xl border border-border p-3 space-y-3 bg-muted/20" data-testid="category-position">
            <p className="text-sm font-semibold">Posizione</p>
            {multiMenus && (
              <div>
                <label className="text-xs font-medium text-muted-foreground">Sposta in un altro menù</label>
                <select value={destMenu} onChange={(e) => setDestMenu(e.target.value)} data-testid="category-menu-select" className="mt-1 w-full rounded-lg border border-input px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#002FA7]">
                  {menus.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                {category && destMenu !== category.menu_id && (
                  <p className="mt-1 text-[11px] text-[#002FA7] font-medium">L'intera categoria con tutti i suoi piatti (e sottocategorie) verrà spostata in questo menù.</p>
                )}
                {(!category || destMenu === category?.menu_id) && (
                  <p className="mt-1 text-[11px] text-muted-foreground">Puoi spostare l'intera categoria (con tutti i suoi piatti) in un altro menù del ristorante.</p>
                )}
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-muted-foreground">Sposta in / annida sotto (nello stesso menù)</label>
              {hasChildren ? (
                <p className="mt-1 text-xs text-amber-600">Questa categoria contiene già delle sottocategorie, quindi resta una categoria principale.</p>
              ) : (
                <select value={parent} onChange={(e) => setParent(e.target.value)} data-testid="category-parent-select" className="mt-1 w-full rounded-lg border border-input px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#002FA7]">
                  <option value={NONE}>Categoria principale (nessuna)</option>
                  {parentOptions.map((c) => <option key={c.id} value={c.id}>↳ dentro “{c.name}”</option>)}
                </select>
              )}
            </div>
          </div>

          <ImageUpload value={cover} onChange={setCover} label="Foto di copertina" testid="category-cover" aspect="aspect-video" />
        </div>
        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <button onClick={onClose} className="rounded-full px-5 py-2 text-sm font-medium hover:bg-muted">Annulla</button>
          <button onClick={save} disabled={saving} data-testid="category-save-btn" className="rounded-full bg-[#002FA7] text-white px-5 py-2 text-sm font-semibold hover:bg-[#00248a] disabled:opacity-60">Salva</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CategoryDialog;
